import { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { WebPushPageProps } from "@/lib/web-push"
import {
  isStandaloneDisplay,
  reconcileWebPushSubscription,
  subscribeToWebPush,
  webPushSupported,
} from "@/lib/web-push"

const DISMISS_KEY = "triyoga-web-push-home-prompt"

export function HomeScreenNotificationsDialog({
  webPush,
}: {
  webPush: WebPushPageProps
}) {
  const [open, setOpen] = useState(false)
  const [denied, setDenied] = useState(false)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function detect() {
      if (!webPush.enabled || !webPush.vapid_public_key) return
      if (!isStandaloneDisplay() || !webPushSupported()) return
      if (sessionStorage.getItem(DISMISS_KEY) === "1") return

      try {
        const existing = await reconcileWebPushSubscription(
          webPush.vapid_public_key,
        )
        if (cancelled || existing) return

        setDenied(Notification.permission === "denied")
        setOpen(true)
      } catch {
        if (cancelled) return

        setMessage(
          "Could not verify this device's notification subscription. Tap Enable to retry.",
        )
        setOpen(true)
      }
    }

    void detect()

    return () => {
      cancelled = true
    }
  }, [webPush.enabled, webPush.vapid_public_key])

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1")
    setOpen(false)
  }

  const enable = async () => {
    if (!webPush.vapid_public_key) return

    setMessage(null)
    setBusy(true)
    try {
      await subscribeToWebPush(webPush.vapid_public_key)
      sessionStorage.removeItem(DISMISS_KEY)
      setOpen(false)
    } catch (error) {
      const name = error instanceof Error ? error.message : ""
      if (name === "permission_denied") {
        setDenied(true)
      } else {
        setMessage(
          "Could not enable Home screen notifications. Please try again.",
        )
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? setOpen(true) : dismiss())}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Turn on Home screen notifications</DialogTitle>
          <DialogDescription>
            Get class reminders and new-content alerts on this Home Screen app.
          </DialogDescription>
        </DialogHeader>

        {denied ? (
          <p className="text-muted-foreground text-sm">
            iPhone blocked notifications for this app. Delete the Home Screen
            icon, remove this site under Settings → Safari → Advanced → Website
            Data, add it again, then tap Enable here.
          </p>
        ) : (
          <p className="text-muted-foreground text-sm">
            Tap Enable, then Allow on the iPhone prompt. You can change this
            later in Settings → Notifications.
          </p>
        )}

        {message && <p className="text-muted-foreground text-sm">{message}</p>}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={dismiss}>
            Not now
          </Button>
          {!denied && (
            <Button type="button" disabled={busy} onClick={() => void enable()}>
              {busy ? "Enabling..." : "Enable"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
